import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, MapPin, CalendarDays, CalendarClock, Tag, ListChecks, MessageSquare, Target, Building2, AlertTriangle, Radar, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ShareDialog } from '@/components/common/ShareDialog';
import { ReportDialog, ContactDialog } from '@/components/common/Dialogs';
import { CompanyAvatar, Chip, DemoBadge, RecentBadge, EmptyState, SponsoredBadge } from '@/components/common/Primitives';
import { OpportunityCard } from '@/components/cards/Cards';
import { MiniStateMap } from '@/components/map/MiniStateMap';
import { useData } from '@/hooks/useData';
import { useIsVisitor, VisitorOnly } from '@/hooks/useIsVisitor';
import { useSeo } from '@/lib/seo';
import { formatDate, isRecent, isExpired, relativeDate } from '@/lib/format';
import { stateName } from '@/data/states';
import { OPP_TYPE_BY_ID, CONTACT_METHODS } from '@/data/taxonomy';
import { supabase } from '@/lib/supabase';
import { companyService, toOpportunity } from '@/services/api';

const OPP_COLS =
  'id,company_id,title,type,category_id,summary,description,requirements,uf,city,target_ufs,published_at,deadline,contact_method,status,sponsored,moderation_flags,created_at,updated_at';
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const Meta = ({ icon: Icon, label, children }) => (
  <div className="flex items-start gap-3 py-3">
    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
    <div className="min-w-0">
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-sm">{children}</div>
    </div>
  </div>
);

export default function OpportunityDetail() {
  const { id } = useParams();
  const { opportunities, companyById, categoryById, publicOpportunities, myCompany } = useData();
  const isVisitor = useIsVisitor();
  const [remote, setRemote] = useState({ id: null, loading: false, opp: null, company: null });

  // Oportunidade encontrada nas listas já carregadas (inclui exemplos e as suas)
  const listed = opportunities.find((x) => x.id === id) || null;
  // Se não estiver nas listas, busca direto no Supabase (ex.: acabou de publicar)
  const needsRemote = !listed && UUID.test(id || '');

  useEffect(() => {
    if (!needsRemote) return undefined;
    let active = true;
    setRemote({ id, loading: true, opp: null, company: null });
    (async () => {
      const { data } = await supabase.from('opportunities').select(OPP_COLS).eq('id', id).maybeSingle();
      const opp = data ? toOpportunity(data) : null;
      const company = opp ? await companyService.get(opp.companyId).catch(() => null) : null;
      if (active) setRemote({ id, loading: false, opp, company });
    })();
    return () => {
      active = false;
    };
  }, [id, needsRemote]);

  const remoteOk = remote.id === id;
  const o = listed || (remoteOk ? remote.opp : null);
  const loading = !listed && needsRemote && (!remoteOk || remote.loading);
  const company = o ? companyById[o.companyId] || (remoteOk ? remote.company : null) : null;

  useSeo({
    title: o ? o.title : loading ? 'Carregando…' : 'Oportunidade não encontrada',
    description: o && company ? `${OPP_TYPE_BY_ID[o.type]?.name} · ${o.location.city}/${o.location.uf} — ${o.summary}` : undefined,
    type: 'article',
  });

  const related = useMemo(
    () => (o ? publicOpportunities.filter((x) => x.id !== o.id && (x.categoryId === o.categoryId || x.type === o.type)).slice(0, 3) : []),
    [publicOpportunities, o]
  );

  const isOwner = !!myCompany && !!o && myCompany.id === o.companyId;
  const visible = !!o && !!company && (isOwner || (company.status === 'ativa' && (o.status === 'ativa' || o.status === 'encerrada')));

  if (loading) {
    return <div className="container-x py-16 text-center text-sm text-muted-foreground">Carregando…</div>;
  }

  if (!visible) {
    return (
      <div className="container-x py-16">
        <EmptyState icon={Radar} title="Oportunidade não encontrada" text="A publicação pode ter sido removida, rejeitada pela moderação ou o link está incorreto." testId="opportunity-not-found" />
        <div className="mt-4 text-center"><Button asChild variant="secondary"><Link to="/oportunidades"><ArrowLeft /> Voltar ao radar</Link></Button></div>
      </div>
    );
  }

  const recent = isRecent(o.publishedAt);
  const expired = isExpired(o.deadline);
  const closed = o.status === 'encerrada' || expired;
  const contact = CONTACT_METHODS.find((m) => m.id === o.contactMethod);
  const contactAvailable = o.contactMethod === 'form' || (o.contactMethod === 'whatsapp' && company.whatsapp) || (o.contactMethod === 'email' && company.email) || (o.contactMethod === 'site' && company.website);

  return (
    <div className="container-x py-8 sm:py-12" data-testid="opportunity-detail">
      <Button asChild variant="ghost" size="sm" className="mb-5 text-muted-foreground"><Link to="/oportunidades"><ArrowLeft /> Radar de oportunidades</Link></Button>

      {o.status === 'em_analise' && (
        <div className="mb-5 flex items-start gap-3 rounded-lg border border-amber-300/25 bg-amber-300/5 px-4 py-3 text-sm text-amber-100" data-testid="opportunity-review-banner">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" /> Esta publicação está em análise pela moderação e só é visível para você. {o.moderationFlags?.length ? `Motivo: ${o.moderationFlags.join(', ')}.` : ''}
        </div>
      )}
      {o.status === 'rejeitada' && isOwner && (
        <div className="mb-5 rounded-lg border border-[rgba(255,59,92,0.3)] bg-[rgba(255,59,92,0.06)] px-4 py-3 text-sm text-[rgb(255,150,165)]">Esta publicação foi rejeitada pela moderação e não está visível ao público.</div>
      )}
      {closed && (
        <div className="mb-5 flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-muted-foreground" data-testid="opportunity-closed-banner">
          <AlertTriangle className="h-4 w-4" /> Esta oportunidade está encerrada{expired ? ' (prazo expirado)' : ''}.
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div className="flex flex-wrap items-center gap-2">
            {recent && <RecentBadge />}
            <Chip className="border-cyan-300/20 bg-cyan-300/5 text-cyan-100">{OPP_TYPE_BY_ID[o.type]?.name}</Chip>
            {o.sponsored && <SponsoredBadge />}
            {o.isDemo && <VisitorOnly><DemoBadge label="Oportunidade fictícia" /></VisitorOnly>}
          </div>
          <h1 className="mt-4 text-3xl font-semibold leading-tight tracking-tight sm:text-4xl" data-testid="opportunity-title">{o.title}</h1>
          <p className="mt-3 text-base text-muted-foreground">{o.summary}</p>
          <div className="mt-6 flex flex-wrap gap-2">
            {!closed && contactAvailable && !isOwner && <ContactDialog company={company} opportunity={o} testId="opportunity-contact-button" />}
            <ShareDialog path={`/oportunidades/${o.id}`} title={o.title} testId="opportunity-share-button" />
            {!isOwner && <ReportDialog targetType="oportunidade" targetId={o.id} testId="opportunity-report-button" />}
          </div>

          <section className="surface mt-8 p-5" data-testid="opportunity-description">
            <h2 className="mb-3 font-display text-base font-semibold">Descrição detalhada</h2>
            <p className="whitespace-pre-line text-sm leading-relaxed text-muted-foreground">{o.description}</p>
          </section>
          <section className="surface mt-5 p-5" data-testid="opportunity-requirements">
            <h2 className="mb-3 flex items-center gap-2 font-display text-base font-semibold"><ListChecks className="h-4 w-4 text-primary" /> Requisitos comerciais</h2>
            {o.requirements?.length ? (
              <ul className="grid gap-2 sm:grid-cols-2">
                {o.requirements.map((r) => <li key={r} className="flex gap-2 rounded-md border border-white/10 bg-white/[0.02] px-3 py-2 text-sm"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />{r}</li>)}
              </ul>
            ) : <p className="text-sm text-muted-foreground">Nenhum requisito específico informado.</p>}
          </section>
          {o.targetUfs?.length > 0 && (
            <section className="surface mt-5 p-5">
              <h2 className="mb-3 flex items-center gap-2 font-display text-base font-semibold"><Target className="h-4 w-4 text-primary" /> Regiões de interesse</h2>
              <div className="flex flex-wrap gap-1.5">{o.targetUfs.map((u) => <Chip key={u}>{stateName(u)}</Chip>)}</div>
            </section>
          )}
        </div>

        <aside className="space-y-5">
          <div className="surface p-5" data-testid="opportunity-company">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Empresa responsável</div>
            <Link to={`/empresas/${company.id}`} className="mt-3 flex items-center gap-3 rounded-md transition-colors hover:text-primary">
              <CompanyAvatar company={company} size={44} />
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold">{company.name}</div>
                <div className="text-xs text-muted-foreground">{categoryById[company.categoryId]?.name}</div>
              </div>
            </Link>
            <Button asChild variant="secondary" size="sm" className="mt-4 w-full border border-white/10"><Link to={`/empresas/${company.id}`}><Building2 /> Ver perfil da empresa</Link></Button>
          </div>
          <div className="surface divide-y divide-white/5 px-5 py-2">
            <Meta icon={MapPin} label="Localização">{o.location.city} · {stateName(o.location.uf)}</Meta>
            <Meta icon={Tag} label="Categoria">{categoryById[o.categoryId]?.name}</Meta>
            <Meta icon={CalendarDays} label="Publicação"><span className="font-mono">{formatDate(o.publishedAt)}</span> <span className="text-muted-foreground">({relativeDate(o.publishedAt)})</span></Meta>
            <Meta icon={CalendarClock} label="Encerramento">{o.deadline ? <span className="font-mono">{formatDate(o.deadline)}</span> : 'Sem data definida'}</Meta>
            <Meta icon={MessageSquare} label="Forma de contato">
              {contactAvailable ? contact?.name : 'Formulário da plataforma'}
              <div className="mt-1 text-[11px] text-muted-foreground">Dados de contato privados só são exibidos com autorização da empresa.</div>
            </Meta>
          </div>
          <MiniStateMap uf={o.location.uf} city={o.location.city} />
        </aside>
      </div>

      {related.length > 0 && (
        <section className="mt-12">
          <h2 className="mb-4 text-xl font-semibold">Oportunidades relacionadas</h2>
          <div className="grid gap-3">{related.map((r) => <OpportunityCard key={r.id} opp={r} dense />)}</div>
        </section>
      )}
    </div>
  );
}